import { useEffect, useRef, useState } from "react";
import type { NixiActivity, NixiColour, NixiVariant } from "./NixiCompanion";

type NixiFrameName =
  | "idle"
  | "toilet-stand"
  | "wave"
  | "get-dressed"
  | "breakfast"
  | "brush-teeth"
  | "pack-bag"
  | "shoes"
  | "fly-complete"
  | "play"
  | "dinner"
  | "get-pajamas"
  | "bedtime";

const activityFrame: Record<Exclude<NixiActivity, "none">, NixiFrameName> = {
  toilet: "toilet-stand",
  gettingDressed: "get-dressed",
  dressed: "pack-bag",
  brushingTeeth: "brush-teeth",
  eating: "breakfast",
  packingBag: "pack-bag",
  shoes: "shoes",
  preschool: "pack-bag",
  snack: "breakfast",
  play: "play",
  dinner: "dinner",
  gettingPajamas: "get-pajamas",
  bedtime: "bedtime",
  bedtimeComplete: "bedtime",
  flyComplete: "fly-complete",
};

const activityMidFrame: Partial<Record<NixiActivity, string>> = {
  gettingDressed: "dress-mid",
  dressed: "pack-bag-mid",
  brushingTeeth: "brush-teeth-mid",
  eating: "breakfast-mid",
  packingBag: "pack-bag-mid",
  shoes: "shoes-mid",
  play: "play-mid",
  dinner: "breakfast-mid",
  bedtime: "bedtime-mid",
};

function frameSource(variant: NixiVariant, frame: string) {
  return `/nixi/action-assets/nixi-${variant}-${frame}.webp`;
}

function shouldRecolourFur(
  red: number,
  green: number,
  blue: number,
  x: number,
  y: number,
  width: number,
  height: number,
  protectOutfit: boolean,
) {
  const lavender = blue > red * 1.035 && red > green * 1.035 && blue > 72;
  const brightFur = (red + green + blue) / 3 > 102;
  // Recolour only the character's fur zones. The previous broad edge test also
  // caught violet outlines in wings, clothing and bags, producing the pink/mint
  // patches reported in the prototype.
  const centralHead = y < height * 0.43 && x > width * 0.18 && x < width * 0.82;
  const hands =
    y >= height * 0.4 &&
    y < height * 0.74 &&
    ((x > width * 0.16 && x < width * 0.33) ||
      (x > width * 0.67 && x < width * 0.84));
  const feet = y >= height * 0.8 && x > width * 0.23 && x < width * 0.77;
  const bareBody =
    !protectOutfit &&
    y >= height * 0.43 &&
    y < height * 0.8 &&
    x > width * 0.2 &&
    x < width * 0.8;
  return lavender && brightFur && (centralHead || hands || feet || bareBody);
}

function RecolouredFrame({
  src,
  colour,
  className,
  protectOutfit = true,
}: {
  src: string;
  colour: NixiColour;
  className: string;
  protectOutfit?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    const image = new Image();
    image.decoding = "async";
    image.src = src;
    image.onload = () => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d", { willReadFrequently: true });
      if (!canvas || !context) return;
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
      if (colour !== "lavender") {
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        const target = colour === "pink" ? [235, 99, 177] : [70, 184, 166];
        for (let offset = 0; offset < pixels.data.length; offset += 4) {
          if (pixels.data[offset + 3] === 0) continue;
          const index = offset / 4;
          const x = index % canvas.width;
          const y = Math.floor(index / canvas.width);
          const red = pixels.data[offset];
          const green = pixels.data[offset + 1];
          const blue = pixels.data[offset + 2];
          if (
            !shouldRecolourFur(
              red,
              green,
              blue,
              x,
              y,
              canvas.width,
              canvas.height,
              protectOutfit,
            )
          )
            continue;
          const light = Math.max(
            0.48,
            Math.min(1.35, (red + green + blue) / 500),
          );
          pixels.data[offset] = Math.min(255, target[0] * light);
          pixels.data[offset + 1] = Math.min(255, target[1] * light);
          pixels.data[offset + 2] = Math.min(255, target[2] * light);
        }
        context.putImageData(pixels, 0, 0);
      }
      setReady(true);
    };
    return () => {
      image.onload = null;
    };
  }, [colour, protectOutfit, src]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className={`${className} ${ready ? "opacity-100" : "opacity-0"}`}
        aria-hidden="true"
      />
      {!ready && (
        <img
          src={src}
          alt=""
          className={className}
          draggable={false}
          aria-hidden="true"
        />
      )}
    </>
  );
}

export function NixiActionFrame({
  variant,
  colour,
  activity,
  home = false,
}: {
  variant: NixiVariant;
  colour: NixiColour;
  activity: NixiActivity;
  home?: boolean;
}) {
  if (home) {
    return (
      <span
        className="jnr-nixi-home-motion absolute inset-0 block"
        data-nixi-rig="home-wave-wing-flutter"
      >
        <RecolouredFrame
          src={frameSource(variant, "home-idle")}
          colour={colour}
          className="jnr-nixi-frame jnr-nixi-frame--idle"
          protectOutfit={false}
        />
        <RecolouredFrame
          src={frameSource(variant, "home-wave-lift")}
          colour={colour}
          className="jnr-nixi-frame jnr-nixi-frame--wave jnr-nixi-frame--wave-lift"
          protectOutfit={false}
        />
        <RecolouredFrame
          src={frameSource(variant, "home-wave-left")}
          colour={colour}
          className="jnr-nixi-frame jnr-nixi-frame--wave jnr-nixi-frame--wave-left"
          protectOutfit={false}
        />
        <RecolouredFrame
          src={frameSource(variant, "home-wave-right")}
          colour={colour}
          className="jnr-nixi-frame jnr-nixi-frame--wave jnr-nixi-frame--wave-right"
          protectOutfit={false}
        />
        <RecolouredFrame
          src={frameSource(variant, "fly-complete")}
          colour={colour}
          className="jnr-nixi-frame jnr-nixi-frame--home-fly"
          protectOutfit
        />
      </span>
    );
  }

  if (activity === "toilet") {
    return (
      <span
        className="jnr-nixi-action jnr-nixi-action--toilet absolute inset-0 block"
        data-nixi-motion="toilet-walk-sequence"
      >
        <RecolouredFrame src={frameSource(variant, "toilet-stand")} colour={colour} className="jnr-nixi-frame jnr-nixi-frame--toilet-stand" protectOutfit={false} />
        <RecolouredFrame src={frameSource(variant, "toilet-step-one")} colour={colour} className="jnr-nixi-frame jnr-nixi-frame--toilet-step-one" protectOutfit={false} />
        <RecolouredFrame src={frameSource(variant, "toilet-step-two")} colour={colour} className="jnr-nixi-frame jnr-nixi-frame--toilet-step-two" protectOutfit={false} />
        <RecolouredFrame src={frameSource(variant, "toilet-reach")} colour={colour} className="jnr-nixi-frame jnr-nixi-frame--toilet-reach" protectOutfit={false} />
      </span>
    );
  }

  if (activity === "gettingDressed") {
    return (
      <span className="jnr-nixi-action jnr-nixi-action--dress-story absolute inset-0 block" data-nixi-motion="dress-story-sequence">
        <RecolouredFrame src={frameSource(variant, "dress-hold")} colour={colour} className="jnr-nixi-frame jnr-nixi-frame--dress-hold" protectOutfit={false} />
        <RecolouredFrame src={frameSource(variant, "dress-overhead")} colour={colour} className="jnr-nixi-frame jnr-nixi-frame--dress-overhead" protectOutfit={false} />
        <RecolouredFrame src={frameSource(variant, "dress-sleeve")} colour={colour} className="jnr-nixi-frame jnr-nixi-frame--dress-sleeve" protectOutfit />
        <RecolouredFrame src={frameSource(variant, "dress-ready")} colour={colour} className="jnr-nixi-frame jnr-nixi-frame--dress-ready" protectOutfit />
      </span>
    );
  }

  const frame = activity === "none" ? "idle" : activityFrame[activity];
  const midFrame = activityMidFrame[activity];
  return (
    <span
      className={`jnr-nixi-action absolute inset-0 block jnr-nixi-action--${frame}${midFrame ? " jnr-nixi-action--sequenced" : ""}`}
      data-nixi-rig={frame}
      data-nixi-motion={midFrame ? "articulated-sequence" : "gentle-loop"}
    >
      <RecolouredFrame
        src={frameSource(variant, frame)}
        colour={colour}
        className={`jnr-nixi-frame${midFrame ? " jnr-nixi-frame--pose-end" : ""}`}
        protectOutfit={frame !== "idle" && frame !== "wave"}
      />
      {midFrame && (
        <RecolouredFrame
          src={frameSource(variant, midFrame)}
          colour={colour}
          className="jnr-nixi-frame jnr-nixi-frame--pose-mid"
          protectOutfit
        />
      )}
    </span>
  );
}

